import type { DehydratedState } from '@tanstack/react-query';

import type { CodeSessionSummary } from './types';

type SavedQuery = DehydratedState['queries'][number];

/** A cloud session list's key (`codeSessionKeys.list`), the one session list saved in the browser. */
function isSessionListKey(queryKey: SavedQuery['queryKey']): boolean {
  return (
    queryKey.length === 2 && queryKey[0] === 'code-sessions' && typeof queryKey[1] === 'number'
  );
}

/**
 * A saved query as it restores: a session list with no session working, any other query as it was.
 * T3 Code never caches a thread with a live run; a saved copy comes from a window that closed, so
 * its "running" is stale when it paints. The first list read says which sessions work now.
 */
export function withNoSessionWorking(query: SavedQuery): SavedQuery {
  if (!isSessionListKey(query.queryKey) || !Array.isArray(query.state.data)) {
    return query;
  }
  // An old window can save a row of another shape; it restores as saved.
  const rows = (query.state.data as (CodeSessionSummary | null)[]).map((row) =>
    row?.status === 'running' || row?.status === 'creating' ? { ...row, status: 'idle' } : row,
  );
  return { ...query, state: { ...query.state, data: rows } };
}
